import React, { useState } from "react";
import { sizes } from "../../constants/sizes";
import styled, { css } from "styled-components";
import { useIntl } from "gatsby-plugin-intl";
import { getMediaPath } from "../../utils/getMediaPath";

type SpImageType = {
  srcSet?: string;
  objectFit?: "contain" | "cover" | "fill";
  objectPosition?: string;
  aspectRatio?: string;
  fill?: boolean;
};

type CustomImagePropsType = {
  src: string;
  alt?: string;
  objectFit?: "contain" | "cover" | "fill";
  objectPosition?: string;
  aspectRatio?: string;
  fill?: boolean;
  sp?: SpImageType;
  maxHeight?: string;
};

const fillStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
`;

export const PictureStyled = styled.picture<{
  $aspectRatio: string;
  $fill: boolean;
  $sp?: SpImageType;
  $objectFit: "contain" | "cover" | "fill";
  $objectPosition: string;
}>`
  display: block;
  width: 100%;
  ${({ $fill, $aspectRatio }) =>
    $fill
      ? fillStyles
      : `
    position: relative;
    aspect-ratio: ${$aspectRatio};
  `}
  img {
    width: 100%;
    height: 100%;
    object-fit: ${(props) => props.$objectFit};
    object-position: ${(props) => props.$objectPosition};
  }
  @media (max-width: ${sizes.breakPoint}px) {
    ${({ $sp, $fill }) => $sp?.fill && !$fill && fillStyles}
    ${({ $sp }) =>
      !$sp?.fill && $sp?.aspectRatio && `aspect-ratio: ${$sp.aspectRatio}`};

    img {
      ${({ $sp }) => $sp?.objectFit && `object-fit: ${$sp.objectFit};`}
      ${({ $sp }) =>
        $sp?.objectPosition && `object-position: ${$sp.objectPosition};`}
    }
  }
`;

/**
 * CustomImage Component
 *
 * @param {string} src - 画像のURL（必須）
 * @param {string} alt - 画像の代替テキスト（任意）
 * @param {"contain" | "cover" | "fill"} objectFit - 画像のフィット方法（任意）
 * @param {string} objectPosition - 画像の位置（任意）
 * @param {string} aspectRatio - アスペクト比（任意）
 * @param {boolean} fill - コンテナに画像をフィルするかどうか（任意）
 * @param {SpImageType} sp - スマートフォン用の画像設定（任意）
 *
 * @example
 * <CustomImage
 *   src="https://example.com/image.jpg"
 *   alt="Sample Image"
 *   objectFit="cover"
 *   objectPosition="center"
 *   aspectRatio="16 / 9"
 *   fill={false}
 *   sp={{
 *     srcSet: "https://example.com/image_sp.jpg",
 *     objectFit: "contain",
 *     objectPosition: "top",
 *     aspectRatio: "4 / 3",
 *     fill: true,
 *   }}
 * />
 */

// WebP パスの自動生成関数
const generateWebpPath = (src: string): string | null => {
  const extensionRegex = /\.(jpg|jpeg|png)$/i;
  if (extensionRegex.test(src)) {
    return src.replace(extensionRegex, ".webp");
  }
  return null;
};

const CustomImage = ({
  src,
  alt = "",
  objectFit = "cover",
  objectPosition = "50% 50%",
  aspectRatio = "16 / 9",
  fill = false,
  lazy = true,
  sp,
}: CustomImagePropsType) => {
  const intl = useIntl();

  const imagePath = getMediaPath({ path: src, locale: intl.locale });
  const webpImagePath = generateWebpPath(imagePath); // WebP 形式のパスを自動生成
  const imagePathSp =
    sp?.srcSet && getMediaPath({ path: sp.srcSet, locale: intl.locale });
  const webpImagePathSp = sp?.srcSet && generateWebpPath(imagePathSp); // SP版のWebPも自動生成

  return (
    <PictureStyled
      $aspectRatio={aspectRatio}
      $fill={fill}
      $objectFit={objectFit}
      $objectPosition={objectPosition}
      $sp={sp}
    >
      {/* WebP形式を優先して読み込む */}
      {webpImagePathSp && (
        <source
          srcSet={webpImagePathSp}
          type="image/webp"
          media={`(max-width: ${sizes.breakPoint}px)`}
        />
      )}
      {imagePathSp && (
        <source
          srcSet={imagePathSp}
          media={`(max-width: ${sizes.breakPoint}px)`}
        />
      )}
      {webpImagePath && <source srcSet={webpImagePath} type="image/webp" />}
      <img src={src} alt={alt} loading={lazy ? "lazy" : "eager"} />
    </PictureStyled>
  );
};

export default CustomImage;
